import React, {useEffect, useState} from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  } from 'recharts';
import _ from 'lodash';
import Loader from "react-loader-spinner";
import {getDataFromLastMonth} from "../../../services/sensorData.service";
import moment from "moment";
import {useTranslation} from "react-i18next";
import CsvDownloader from 'react-csv-downloader';

interface SmLineProps {
  width: number;
  names: any[];
  allNames: any;
}
const initialState = {
    left : 'dataMin',
    right : 'dataMax',
    refAreaLeft : '',
    refAreaRight : '',
    top : 'dataMax+1',
    bottom : 'dataMin-1',
    top2 : 'dataMax+20',
    bottom2 : 'dataMin-20',
    animation : true
};

const allColumns = [
    {
        id: 'temp1',
        displayName: 'temp1'
    },
    {
        id: 'temp2',
        displayName: 'temp2'
    },
    {
        id: 'temp3',
        displayName: 'temp3'
    },
    {
        id: 'temp4',
        displayName: 'temp4'
    },
    {
        id: 'temp5',
        displayName: 'temp6'
    },
    {
        id: 'humidity1',
        displayName: 'humidity1'
    },
    {
        id: 'Power',
        displayName: 'power consumption'
    },
    {
        id: 'voltageInput',
        displayName: 'voltage'
    }
]

interface IChartData {
    timestamp: string;
    temp1: number;
    temp2: number;
    temp3: number;
    temp4: number;
    temp5: number;
    temp6: number;
}

const MAX_NUMBER_OF_DISPLAYED_ITEMS = 5000;

const createAvg = (listOfItems: any[], timestamp: string) => {
    let srednia: IChartData = {
        // timestamp: moment(d.timestamp, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY hh:00'),
        timestamp: timestamp,
        temp1: 0,
        temp2: 0,
        temp3: 0,
        temp4: 0,
        temp5: 0,
        temp6: 0
    };
    listOfItems.forEach((hourData) => {
        srednia = {
            timestamp: srednia.timestamp,
            temp1: srednia.temp1 + hourData.temp1,
            temp2: srednia.temp2 + hourData.temp2,
            temp3: srednia.temp3 + hourData.temp3,
            temp4: srednia.temp4 + hourData.temp4,
            temp5: srednia.temp5 + hourData.temp5,
            temp6: srednia.temp6 + hourData.temp6,

        }
    })
    const sredniaToAdd = {
        timestamp: srednia.timestamp,
        temp1: srednia.temp1 / listOfItems.length,
        temp2: srednia.temp2 / listOfItems.length,
        temp3: srednia.temp3 / listOfItems.length,
        temp4: srednia.temp4 / listOfItems.length,
        temp5: srednia.temp5 / listOfItems.length,
        temp6: srednia.temp6 / listOfItems.length,
    }
    return sredniaToAdd;
}

const SmLine: React.FC<SmLineProps> = (props: SmLineProps) => {
    const { t } = useTranslation();
    const defaultColors = [
        '#E52B50',
        '#89CFF0',
        '#8A2BE2',
        '#CD7F32',
        '#7FFF00',
        '#FFD700'
    ];
    const [defaultShowedValues, setDefaultShowedValues] = useState([...props.names.map((name) => name.key), 'timestamp']);
    const [defaultData, setDefaultData] = useState<any[]>([])
    const [dataToShow, setDataToShow] = useState<any[]>(defaultData);
    const [loading, setLoading] = useState(true);
    const [mainState, setMainState] = useState(initialState);
    const [translatedColumns, setTranslatedColumns] = useState(allColumns);
    useEffect(() => {
        const trans = allColumns.map((item) => {
            if(item.id.includes('temp')) {
                return {
                    id: item.id,
                    displayName: _.get(props.allNames, 'temperature.' + item.id, item.displayName)
                }
            } else if (item.id.includes('humidity1')) {
                return {
                    id: item.id,
                    displayName: _.get(props.allNames, 'humidity.humidity1', item.displayName)
                }
            }
            return {
                id: item.id,
                displayName: item.displayName
            }
        })
        setTranslatedColumns(trans);
    }, [props.allNames])
    const setCorrectData = (dataToDisplay: any[]) => {
        console.log('Numbers of items: ', dataToDisplay.length);
        if (dataToDisplay.length > MAX_NUMBER_OF_DISPLAYED_ITEMS && dataToDisplay.length > 0) {
            const newDataToDisplay: IChartData[] = [];
            let hourData: IChartData[] = [];
            dataToDisplay.forEach((d: IChartData, index) => {
                if ((hourData.length === 0 || moment(d.timestamp, 'DD-MM-YYYY HH:mm').isSame(moment(hourData[0].timestamp, 'DD-MM-YYYY HH:mm'), 'hour'))) {
                    hourData.push(d);
                    if (dataToDisplay.length - 1 === index) {
                        newDataToDisplay.push(createAvg(hourData, moment(d.timestamp, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY HH:00')));
                        hourData = [];
                    }
                } else {
                    newDataToDisplay.push(createAvg(hourData, moment(d.timestamp, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY HH:00')));
                    hourData = [];
                }
            });
            console.log('newDataToDisplay: ', newDataToDisplay)
            setDataToShow(newDataToDisplay.map((item) => ({
                timestamp: item.timestamp,
                temp1: item.temp1.toFixed(2),
                temp2: item.temp2.toFixed(2),
                temp3: item.temp3.toFixed(2),
                temp4: item.temp4.toFixed(2),
                temp5: item.temp5.toFixed(2),
                temp6: item.temp6.toFixed(2),
            })));
        } else {
            console.log('dataToDisplay: ', dataToDisplay)
            setDataToShow(dataToDisplay.map((item) => ({
                timestamp: item.timestamp,
                temp1: item.temp1.toFixed(2),
                temp2: item.temp2.toFixed(2),
                temp3: item.temp3.toFixed(2),
                temp4: item.temp4.toFixed(2),
                temp5: item.temp5.toFixed(2),
                temp6: item.temp6.toFixed(2),
            })));
        }
    }
    useEffect(() => {
        // @ts-ignore
        const dataToDisplay: IChartData[] = defaultData.map((d) => _.pick(d, [...defaultShowedValues]));
        setCorrectData(dataToDisplay);
    }, [props.names, defaultShowedValues, defaultData]);
    useEffect(() => {
        getDataFromLastMonth().then(
            (res) => {
                const d = res || [];
                setLoading(false);
                const sortedData = _.orderBy(d, 'timestamp', 'asc');
                const dataToSet = _.uniqBy(sortedData.map((item: any) => _.assign({}, item, {
                    timestamp: moment.unix(_.get(item, 'timestamp', 0)).add(-1, 'hour').format('DD-MM-YYYY HH:mm')
                })), 'timestamp').map((item) => {
                    return {
                        timestamp: item.timestamp,
                        temp1: parseFloat(_.get(item, 'temp1', '0')),
                        temp2: parseFloat(_.get(item, 'temp2', '0')),
                        temp3: parseFloat(_.get(item, 'temp3', '0')),
                        temp4: parseFloat(_.get(item, 'temp4', '0')),
                        temp5: parseFloat(_.get(item, 'temp5', '0')),
                        temp6: parseFloat(_.get(item, 'temp6', '0')),
                        humidity1: parseFloat(_.get(item, 'humidity1', '0')),
                        'Power': parseFloat(_.get(item, 'Power', '0')),
                        'voltageInput': parseFloat(_.get(item, 'voltageInput', 0).toString())
                    }
                })
                console.log('dataToSet: ', dataToSet)
                setDefaultData(dataToSet);
            }
        )
    }, []);

    const zoomIn = () => {
        if ((!mainState.refAreaLeft || !mainState.refAreaRight) || mainState.refAreaLeft === mainState.refAreaRight || mainState.refAreaRight === '' ) {
            setMainState(_.assign(mainState, {
                refAreaLeft : '',
                refAreaRight : ''
            }))
            return;
        } else {
            const newData = defaultData.filter((item) => item.timestamp >= mainState.refAreaLeft && item.timestamp <= mainState.refAreaRight);
            setCorrectData(newData.map((d) => _.pick(d, [...defaultShowedValues])));
        }
    }
    const zoomOut = () => {
        setCorrectData(defaultData.map((d) => _.pick(d, [...defaultShowedValues])));
    }
    const handleClickOnLegend = (event: any) => {
        const key = event.dataKey;
        if (defaultShowedValues.includes(key)) {
            setDefaultShowedValues(defaultShowedValues.filter((val) => val !== key));
        } else {
            setDefaultShowedValues([...defaultShowedValues, key])
        }
    }
    if (loading) {
        return <div className='w-100 d-flex justify-content-center pt-5'>
            <Loader
                type="Puff"
                color="#555374"
                height={100}
                width={100}
                timeout={200000} //3 secs

            /></div>
    }
    const getColumns = () => {
        const cloned = _.clone(translatedColumns);
        return [{
            id: 'timestamp',
            displayName: 'timestamp'
        },...cloned];
    }
    return (
        <div>
            <div className="d-flex justify-content-end right-button-content">
                <CsvDownloader
                    filename="myfile"
                    separator=";"
                    wrapColumnChar="'"
                    columns={getColumns()}
                    datas={defaultData}>
                    <button className="btn btn-outline-secondary right-button">{t('download')}</button>
                </CsvDownloader>
                <button onClick={zoomOut} className="btn btn-outline-secondary right-button">{t('zoomOut')} </button>
            </div>
            <LineChart
                data={dataToShow}
                width={props.width}
                height={300}
                onMouseDown = { (e) => setMainState(_.assign(mainState, {
                    refAreaLeft: e.activeLabel
                })) }
                onMouseMove = { (e) => mainState.refAreaLeft && setMainState(_.assign(mainState, {refAreaRight:e.activeLabel})) }
                onMouseUp = { zoomIn }
                margin={{
                    top: 5, right: 30, left: 20, bottom: 5,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis />
                <Tooltip />
                <Legend onClick={handleClickOnLegend} wrapperStyle={{
                    'padding-top': "20px"
                }} />
                {
                    props.names.map((name, index) =>
                        <Line
                            type="monotone"
                            dataKey={name.key}
                            name={name.name}
                            stroke={defaultColors[index] || defaultColors[0]}
                        />)
                }
            </LineChart>
        </div>
      );
}

export default SmLine;